package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.AccountUseCase
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.todayIn
import kotlin.time.Clock

sealed class RecentSpendingScreenState {
    data object Loading : RecentSpendingScreenState()
    data class Error(val message: String) : RecentSpendingScreenState()
    data class Loaded(
        val categories: List<CategorySpendBreakdown>,
        val totalSpending: Double,
        val selectedRange: SpendingRange
    ) : RecentSpendingScreenState()
}

data class CategorySpendBreakdown(
    val category: String,
    val amount: Double,
    val transactions: List<Transaction>
)

@OptIn(ExperimentalCoroutinesApi::class)
class RecentSpendingViewModel(
    private val accountUseCase: AccountUseCase,
    private val transactionRepository: TransactionRepository,
    private val dashboardUseCase: DashboardUseCase,
    private val recentSpendingManager: RecentSpendingManager
) : ViewModel() {

    private val _state = MutableStateFlow<RecentSpendingScreenState>(RecentSpendingScreenState.Loading)
    val state = _state.asStateFlow()

    init {
        startFlowCollection()
    }

    private fun startFlowCollection() {
        viewModelScope.launch {
            val dashboardsDetailsFlow = dashboardUseCase.GetDashboards().flatMapLatest { dashboardsResponse ->
                when (dashboardsResponse) {
                    is Response.Error -> flowOf(Response.Error(dashboardsResponse.message))
                    Response.Loading -> flowOf(Response.Loading)
                    is Response.Success -> {
                        val dashboards = dashboardsResponse.data
                        if (dashboards.isEmpty()) {
                            flowOf(Response.Success(emptyList<DashboardDetail>()))
                        } else {
                            val detailFlows = dashboards.map { dashboard ->
                                dashboardUseCase.GetDashboardDetail(dashboard.dashboardId)
                            }
                            combine(detailFlows) { detailsResponses ->
                                val error = detailsResponses.filterIsInstance<Response.Error>().firstOrNull()
                                if (error != null) {
                                    Response.Error(error.message)
                                } else if (detailsResponses.any { it is Response.Loading }) {
                                    Response.Loading
                                } else {
                                    val details = detailsResponses.filterIsInstance<Response.Success<DashboardDetail>>().map { it.data }
                                    Response.Success(details)
                                }
                            }
                        }
                    }
                }
            }

            combine(
                dashboardsDetailsFlow,
                accountUseCase.GetAccounts(),
                transactionRepository.transactions,
                recentSpendingManager.selectedRange
            ) { detailsResponse, accountsResponse, transactionsResponse, selectedRange ->
                when {
                    detailsResponse is Response.Error ->
                        RecentSpendingScreenState.Error(detailsResponse.message)
                    accountsResponse is Response.Error ->
                        RecentSpendingScreenState.Error(accountsResponse.message)
                    transactionsResponse is Response.Error ->
                        RecentSpendingScreenState.Error(transactionsResponse.message)
                    detailsResponse is Response.Success && accountsResponse is Response.Success && transactionsResponse is Response.Success -> {
                        val dashboards = detailsResponse.data
                        val dashboardTransactions = dashboards
                            .flatMap { it.transactions }
                            .distinctBy { it.transactionId }

                        if (accountsResponse.data.isEmpty()) {
                            RecentSpendingScreenState.Loaded(emptyList(), 0.0, selectedRange)
                        } else {
                            val rangeDateString = try {
                                val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                                when (selectedRange) {
                                    SpendingRange.SevenDays -> today.minus(7, DateTimeUnit.DAY).toString()
                                    SpendingRange.OneMonth -> today.minus(1, DateTimeUnit.MONTH).toString()
                                    SpendingRange.ThreeMonths -> today.minus(3, DateTimeUnit.MONTH).toString()
                                    SpendingRange.SixMonths -> today.minus(6, DateTimeUnit.MONTH).toString()
                                    SpendingRange.OneYear -> today.minus(1, DateTimeUnit.YEAR).toString()
                                    SpendingRange.YearToDate -> LocalDate(today.year, 1, 1).toString()
                                }
                            } catch (e: Exception) {
                                ""
                            }

                            val recentSpending = dashboardTransactions.filter { tx ->
                                tx.date >= rangeDateString && tx.amount > 0 && !tx.isExcludedFromSpending()
                            }

                            val total = recentSpending.sumOf { it.amount }

                            val grouped = recentSpending.groupBy { tx ->
                                tx.getRecentSpendingCategoryBreakdownKey()
                            }

                            val breakdowns = grouped.map { (catName, txs) ->
                                CategorySpendBreakdown(
                                    category = catName,
                                    amount = txs.sumOf { it.amount },
                                    transactions = txs.sortedByDescending { it.date }
                                )
                            }.sortedByDescending { it.amount }

                            RecentSpendingScreenState.Loaded(breakdowns, total, selectedRange)
                        }
                    }
                    else -> RecentSpendingScreenState.Loading
                }
            }.collect { newState ->
                val currentState = _state.value
                if (newState is RecentSpendingScreenState.Loading && currentState is RecentSpendingScreenState.Loaded) {
                    // Keep the current loaded state to prevent flashing a loading spinner on refresh
                } else {
                    _state.value = newState
                }
            }
        }
    }

    fun load() {
        viewModelScope.launch {
            transactionRepository.refresh()
        }
    }

    fun setRange(range: SpendingRange) {
        recentSpendingManager.setRange(range)
    }
}
